export const MAP_ASSET_VERSION = "2000879" as const;

export type MapFloor = "upper" | "lower";

export interface MapVerticalSection {
  minZ: number;
  maxZ: number;
}

export interface MapConfig {
  name: string;
  key: string;
  posX: number;
  posY: number;
  scale: number;
  rotate?: boolean;
  radarSize?: number;
  verticalSections?: Partial<Record<MapFloor, MapVerticalSection>>;
  radarImage: string;
  lowerRadarImage?: string;
  geometryUrl: string;
  navUrl: string;
}

export interface MapAssetConfig {
  name: string;
  key: string;
  geometryUrl: string;
  navUrl: string;
}

function assetUrl(map: string, asset: "radar" | "radar-lower" | "geometry" | "nav") {
  return `/api/maps/${map}/${asset}?version=${MAP_ASSET_VERSION}`;
}

function mapConfig(
  key: string,
  name: string,
  posX: number,
  posY: number,
  scale: number,
  options: {
    rotate?: boolean;
    floorThreshold?: number;
  } = {}
): MapConfig {
  const hasFloors = options.floorThreshold != null;
  return {
    key,
    name,
    posX,
    posY,
    scale,
    rotate: options.rotate,
    radarSize: 2048,
    radarImage: assetUrl(key, "radar"),
    ...(hasFloors
      ? {
          lowerRadarImage: assetUrl(key, "radar-lower"),
          verticalSections: {
            upper: { minZ: options.floorThreshold!, maxZ: 20_000 },
            lower: { minZ: -10_000, maxZ: options.floorThreshold! },
          },
        }
      : {}),
    geometryUrl: assetUrl(key, "geometry"),
    navUrl: assetUrl(key, "nav"),
  };
}

const INSTALLED_MAPS: Record<string, string> = {
  ar_baggage: "Baggage",
  ar_pool_day: "Pool Day",
  ar_shoots: "Shoots",
  ar_shoots_night: "Shoots Night",
  cs_italy: "Italy",
  cs_office: "Office",
  cs_shelter: "Shelter",
  de_ancient: "Ancient",
  de_ancient_night: "Ancient Night",
  de_anubis: "Anubis",
  de_boulder: "Boulder",
  de_cache: "Cache",
  de_debris: "Debris",
  de_dust2: "Dust 2",
  de_eldorado: "Eldorado",
  de_fachwerk: "Fachwerk",
  de_inferno: "Inferno",
  de_mirage: "Mirage",
  de_nuke: "Nuke",
  de_overpass: "Overpass",
  de_poseidon: "Poseidon",
  de_train: "Train",
  de_vertigo: "Vertigo",
};

export const MAP_ASSET_CONFIGS: Record<string, MapAssetConfig> = Object.fromEntries(
  Object.entries(INSTALLED_MAPS).map(([key, name]) => [key, {
    key,
    name,
    geometryUrl: assetUrl(key, "geometry"),
    navUrl: assetUrl(key, "nav"),
  }])
);

// Exact transforms and vertical sections from awpy-data ClientVersion 2000879.
export const MAP_CONFIGS: Record<string, MapConfig> = {
  ar_baggage: mapConfig("ar_baggage", "Baggage", -1316, 1288, 2.539062, { rotate: true, floorThreshold: -5 }),
  ar_shoots: mapConfig("ar_shoots", "Shoots", -1368, 1952, 2.6875),
  ar_shoots_night: mapConfig("ar_shoots_night", "Shoots Night", -1368, 1952, 2.6875),
  cs_italy: mapConfig("cs_italy", "Italy", -2647, 2592, 4.6, { rotate: true }),
  cs_office: mapConfig("cs_office", "Office", -1838, 1858, 4.1),
  de_dust2: mapConfig("de_dust2", "Dust 2", -2476, 3239, 4.4, { rotate: true }),
  de_mirage: mapConfig("de_mirage", "Mirage", -3230, 1713, 5),
  de_inferno: mapConfig("de_inferno", "Inferno", -2087, 3870, 4.9),
  de_nuke: mapConfig("de_nuke", "Nuke", -3453, 2887, 7, { floorThreshold: -495 }),
  de_overpass: mapConfig("de_overpass", "Overpass", -4831, 1781, 5.2),
  de_vertigo: mapConfig("de_vertigo", "Vertigo", -3168, 1762, 4, { floorThreshold: 11700 }),
  de_ancient: mapConfig("de_ancient", "Ancient", -2953, 2164, 5),
  de_anubis: mapConfig("de_anubis", "Anubis", -2796, 3328, 5.22),
  de_cache: mapConfig("de_cache", "Cache", -2000, 3250, 5.5),
  de_ancient_night: mapConfig("de_ancient_night", "Ancient Night", -2953, 2164, 5),
  de_train: mapConfig("de_train", "Train", -2308, 2078, 4.082077, { floorThreshold: -50 }),
};

const MAP_ALIASES: Record<string, string> = { cache: "de_cache" };

export function getMapConfig(mapName: string): MapConfig | null {
  const normalizedName = mapName.toLowerCase();
  return MAP_CONFIGS[MAP_ALIASES[normalizedName] ?? normalizedName] ?? null;
}

export function getMapAssetConfig(mapName: string): MapAssetConfig | null {
  const normalizedName = mapName.toLowerCase();
  return MAP_ASSET_CONFIGS[MAP_ALIASES[normalizedName] ?? normalizedName] ?? null;
}

export function getMapRadarSize(mapConfig: MapConfig | null) {
  return mapConfig?.radarSize ?? 1024;
}

export function getMapFloor(mapConfig: MapConfig | null, z?: number | null): MapFloor {
  if (!mapConfig?.verticalSections?.lower || z == null || !Number.isFinite(z)) {
    return "upper";
  }
  return z < mapConfig.verticalSections.upper!.minZ ? "lower" : "upper";
}

export function getMapRadarImage(mapConfig: MapConfig, floor: MapFloor | "all") {
  return floor === "lower" && mapConfig.lowerRadarImage
    ? mapConfig.lowerRadarImage
    : mapConfig.radarImage;
}

export function gameToRadarPoint(mapConfig: MapConfig, x: number, y: number) {
  const radarSize = getMapRadarSize(mapConfig);
  const scaleFactor = radarSize / 1024;
  return {
    x: ((x - mapConfig.posX) / mapConfig.scale) * scaleFactor,
    y: ((mapConfig.posY - y) / mapConfig.scale) * scaleFactor,
  };
}
